// src/components/upload/Upload.js - Minimal changes for autoOpen
import React, { useState, useEffect, useRef } from 'react';

import { 
  FaRegFilePdf, 
  FaRegFileLines, 
  FaRegImage, 
  FaRegEnvelope,
} from 'react-icons/fa6';

import { 
  PiPresentationChartBold,
 } from 'react-icons/pi';

import { FilePlus2, Table2, Files } from 'lucide-react';

/**
 * Enhanced Upload Component - Button floats and fades
 */
const EnhancedUpload = ({ 
  onFilesAdded, 
  disabled = false,
  autoOpen = false, // If true (no files), opens automatically and stays open
  hasSelections = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const dropZoneRef = useRef(null);
  const buttonRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // File type configurations
  const fileTypes = [
    {
      icon: FaRegFilePdf,
      extensions: '.pdf'
    },
    {
      icon: FaRegFileLines,
      extensions: '.docx, .txt'
    },
    {
      icon: FaRegImage,
      extensions: '.jpg, .png'
    },
    {
      icon: Table2,
      extensions: '.xlsx'
    },
    {
      icon: PiPresentationChartBold,
      extensions: '.pptx'
    },
    {
      icon: FaRegEnvelope,
      extensions: '.eml'
    }
  ];
  
  useEffect(() => {
    if (autoOpen) {
      // Force close first, then reopen after a brief delay
      // This ensures proper animation even when manually opened before switching cases
      setIsExpanded(false);
      setShowButton(true);
      
      // Wait for close animation, then open
      const timer = setTimeout(() => {
        setIsExpanded(true);
        setShowButton(false);
      }, 100); // Brief delay to ensure close completes first
      
      return () => clearTimeout(timer);
    } else {
      // When files exist, reset to normal state (closed, button visible)
      setIsExpanded(false);
      setShowButton(true);
    }
  }, [autoOpen]);

  // Handle click outside to collapse (MODIFIED: skip if autoOpen)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // NEW: Don't close on click outside if autoOpen (no files)
      if (autoOpen) return;
      
      if (
        dropZoneRef.current && 
        !dropZoneRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowContent(false);
        setTimeout(() => setIsExpanded(false), 150);
      }
    };
    
    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, autoOpen]);
  
  // Handle content animation timing
  useEffect(() => {
    if (isExpanded) {
      const timer = setTimeout(() => setShowContent(true), 150);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
    }
  }, [isExpanded]);
  
  // Handle button fade animation
  useEffect(() => {
    if (isExpanded) {
      // Fade out button after a brief delay (let zone start expanding first)
      const timer = setTimeout(() => {
        setShowButton(false);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Fade button back in immediately when collapsing
      setShowButton(true);
    }
  }, [isExpanded]);
  
  // Global drag detection
  useEffect(() => {
    if (disabled) return;
    
    let dragCounter = 0;
    
    const handleDragIn = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter++;
      
      if (e.dataTransfer.types && Array.from(e.dataTransfer.types).includes('Files')) {
        setIsDragActive(true);
        setIsExpanded(true);
      }
    };
    
    const handleDragOut = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter--;
      
      if (dragCounter === 0) {
        setIsDragActive(false);
        setShowContent(false);
        setTimeout(() => setIsExpanded(false), 100);
      }
    };
    
    const handleDrag = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
      dragCounter = 0;
      
      if (disabled) return;
      
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const filesArray = Array.from(e.dataTransfer.files);
        console.log('Drag and drop - files:', filesArray.length, filesArray.map(f => f.name));
        onFilesAdded(filesArray);
        setShowContent(false);
        setTimeout(() => setIsExpanded(false), 100);
      }
    };
    
    window.addEventListener('dragenter', handleDragIn);
    window.addEventListener('dragleave', handleDragOut);
    window.addEventListener('dragover', handleDrag);
    window.addEventListener('drop', handleDrop);
    
    return () => {
      window.removeEventListener('dragenter', handleDragIn);
      window.removeEventListener('dragleave', handleDragOut);
      window.removeEventListener('dragover', handleDrag);
      window.removeEventListener('drop', handleDrop);
    };
  }, [onFilesAdded, disabled]);
  
  // Handle file input change
  const handleFileInputChange = (e) => {
    if (disabled) return;
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      console.log('File input - files:', filesArray.length, filesArray.map(f => f.name));
      onFilesAdded(filesArray);
      setShowContent(false);
      setTimeout(() => setIsExpanded(false), 100);
      e.target.value = '';
    }
  };
  
  // Open file dialog
  const handleChooseFiles = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };
  
  // Toggle expanded state
  const handleButtonClick = () => {
    if (disabled) return;
    setIsExpanded(!isExpanded);
  };
  
  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.eml,.msg,.txt,.rtf"
        multiple
        disabled={disabled}
        onChange={handleFileInputChange}
      />
      
      {/* Drop Zone - In document flow, grows downwards */}
      <div
        ref={dropZoneRef}
        className={`
          m-app-md
          bg-graph-paper rounded-app-md
          overflow-hidden
          flex-shrink-0
          transition-all duration-app-normal ease-out
          ${isExpanded ? 'h-48' : 'h-0'}
        `}
        style={{
          transformOrigin: 'bottom',
        }}
      >
        {/* Content inside drop zone */}
        <div className="h-full flex flex-col justify-center items-center py-app-lg px-app-md">
          <div className={`w-full transition-all duration-200 ${
            showContent 
              ? 'opacity-100 transform translate-y-0' 
              : 'opacity-0 transform translate-y-1'
          }`}>
            {showContent && (
              <>
                {/* FIXED #3 & #4: File type icons at top - centered with gap, individual hover states */}
                <div className="flex justify-center items-start gap-x-app-md gap-y-app-lg flex-wrap">
                  {fileTypes.map((type, index) => (
                    <div 
                      key={index}
                      className="relative group px-0"
                    >
                      <div className="flex justify-center transition-transform duration-200 group-hover:-translate-y-1">
                        <type.icon 
                          className="text-app-gray-900" 
                          size={20} 
                        />
                      </div>
                      
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        <div className="text-app-xs text-app-gray-500 whitespace-nowrap">
                          {type.extensions}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* First text line */}
                <div className="text-center mt-app-xl">
                  <p className="text-app-md font-medium text-app-gray-900">
                    Drop files here
                  </p>
                </div>
                
                {/* Second text line */}
                <div className="text-center mt-0">
                  <p className="text-app-xs text-app-gray-500">
                    or
                  </p>
                </div>
                
                {/* FIXED #1: Button - centered */}
                <div className="mt-app-sm flex justify-center">
                  <button
                    onClick={handleChooseFiles}
                    disabled={disabled}
                    className="btn-primary"
                  >
                    <Files size={16} />
                    Choose Files
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Floating Button - Absolutely positioned to outer container */}
      {/* h-56 = 224px, mb-app-lg = 16px, gap = 8px => total 248px when expanded */}
      {/* When collapsed: 8px gap from bottom and right */}
      <div 
        className={`absolute right-0 mr-app-xl mb-app-sm z-20 transition-all duration-app-normal ease-out ${
          isExpanded ? 'bottom-[248px]' : 'bottom-app-sm'
        } ${
          showButton && !hasSelections ? 'opacity-100' : 'opacity-0 pointer-events-none'  // MODIFY THIS
        } ${
          hasSelections ? 'translate-y-[-60px]' : 'translate-y-0'  // ADD THIS
        }`}
      >
        <div
          ref={buttonRef}
          className={`w-[56px] h-[32px] cursor-pointer ${
            disabled
              ? 'opacity-50 cursor-not-allowed border-app-gray-400 bg-app-gray-200'
              : `border-0 bg-app-gray-300 hover:bg-app-gray-400 ${showButton ? 'shadow-app-outer' : 'shadow-none'}`
          } rounded-[21px]`}
          onClick={handleButtonClick}
        >
          <div className="flex items-center justify-center h-full">
            <FilePlus2 className="text-app-gray-900 m-app-sm" size={16} />
          </div>
        </div>
      </div>
    </>
  );
};

export default EnhancedUpload;